import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { intlShape } from 'react-intl';
import { connectToStores } from 'fluxible-addons-react';
import getJson from '@digitransit-search-util/digitransit-search-util-get-json';
import MenuItem from './MenuItem';
import { addAnalyticsEvent } from '../util/analyticsUtils';

/**
 * REIS-196 (WCAG 2.4.5 Multiple Ways): a site footer with a navigation
 * landmark linking the CMS content pages, giving a second, menu-independent
 * way to reach those pages. Visible on every page. Also serves as the page's
 * contentinfo landmark (it is a top-level sibling of <main>, not inside it).
 */
function Footer({ currentLanguage }, { config, intl }) {
  const [links, setLinks] = useState([]);

  useEffect(() => {
    let active = true;
    getJson(`${config.URL.CONTENT_PAGE_URL}/${currentLanguage}`)
      .then(result => {
        if (active) {
          setLinks(
            result.map(item => ({
              label: item.label,
              href: item.href,
              route: !item.href ? `/content/${item.key}` : undefined,
              openInNewTab: !!item.href,
            })),
          );
        }
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [currentLanguage]);

  if (!links.length) {
    return null;
  }

  return (
    <footer className="page-footer" role="contentinfo">
      <nav
        aria-label={intl.formatMessage({
          id: 'footer-navigation',
          defaultMessage: 'Footer navigation',
        })}
      >
        <ul className="page-footer-links">
          {links.map(link => (
            <li key={link.label || link.route || link.href}>
              <MenuItem
                {...link}
                onClick={() =>
                  addAnalyticsEvent({
                    category: 'Navigation',
                    action: 'OpenFooterLink',
                    name: link.label,
                  })
                }
              />
            </li>
          ))}
        </ul>
      </nav>
      {config.menu?.copyright?.label && (
        <div className="page-footer-copyright">
          {config.menu.copyright.label}
        </div>
      )}
      {config.menu?.email?.label && (
        <a
          className="page-footer-email"
          href={`mailto:${config.menu.email.label}`}
        >
          {config.menu.email.label}
        </a>
      )}
    </footer>
  );
}

Footer.propTypes = {
  currentLanguage: PropTypes.string.isRequired,
};

Footer.contextTypes = {
  config: PropTypes.object.isRequired,
  intl: intlShape.isRequired,
};

export default connectToStores(
  Footer,
  ['PreferencesStore'],
  ({ getStore }) => ({
    currentLanguage: getStore('PreferencesStore').getLanguage(),
  }),
);
